import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";

import { handleGetPublicPitchById } from "../../shared/controllers/pitchController";
import { unwrapApiData } from "../../shared/utils/apiResponse";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface Pitch {
    name: string;
    location: string;
    image?: string;
}

const decodeSharedId = (value: string | null | undefined): string | null => {
    if (!value) return null;

    const raw = value.trim();
    if (!raw) return null;

    try {
        const normalized = raw.replace(/-/g, "+").replace(/_/g, "/");
        const padding = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
        const decoded = atob(normalized + padding).trim();

        if (/^\d+$/.test(decoded)) {
            return decoded;
        }
    } catch {
        // use raw id
    }

    return raw;
};

const DetailedPitch: React.FC = () => {
    const { id: routeId } = useParams();
    const [searchParams] = useSearchParams();
    const pitchId = decodeSharedId(routeId) || decodeSharedId(searchParams.get("id"));
    const [pitch, setPitch] = useState<Pitch | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPitch = async () => {
            if (!pitchId) {
                setLoading(false);
                return;
            }
            try {
                const response = await handleGetPublicPitchById(pitchId);
                const resolvedPitch = unwrapApiData<Pitch>(response);
                if (resolvedPitch) {
                    setPitch(resolvedPitch);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchPitch();
    }, [pitchId]);

    if (loading) return <div className="py-24"><LoadingGrid count={1} /></div>;
    if (!pitch) return <div className="h-screen flex items-center justify-center font-Manrope font-black text-gray-400 text-2xl italic">Pitch not found.</div>;

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white font-Manrope">
                {/* Pitch Hero Selection */}
                <div className="grid grid-cols-1 lg:grid-cols-2 h-[80vh] min-h-[600px] border-b border-gray-100">
                    <div className="relative overflow-hidden h-full">
                        <img src={pitch.image || landingAsset("stadia.svg")} className="w-full h-full object-cover" alt="" />
                        <div className="absolute inset-0 bg-black/10" />
                    </div>
                    <div className="flex flex-col justify-center p-8 md:p-20 space-y-12">
                        <div className="space-y-4">
                            <span className="px-6 py-2 bg-green-700 text-white rounded-full font-black text-xs uppercase tracking-widest shadow-xl">
                                Stadia Select
                            </span>
                            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 uppercase tracking-tighter leading-[0.85]">
                                {pitch.name}
                            </h1>
                        </div>

                        <div className="space-y-8">
                            <div className="flex items-center gap-6">
                                <span className="text-4xl">📍</span>
                                <div>
                                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Location</p>
                                    <p className="text-3xl font-black text-gray-900 uppercase tracking-tight">{pitch.location || "Multiple Locations"}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                {["Natural Grass", "FIFA Certified", "Underflood Lights", "Private Parking"].map((feat, i) => (
                                    <span key={i} className="px-6 py-3 bg-gray-50 border border-gray-100 rounded-2xl font-black text-xs text-gray-400 uppercase tracking-widest">
                                        {feat}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="pt-12">
                            <Link to="/sign-up" className="inline-block px-12 py-5 bg-green-700 hover:bg-green-800 text-white rounded-3xl font-black text-xl transition-all shadow-xl active:scale-95 shadow-green-200">
                                Book for Match
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 md:px-14 py-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        <div className="bg-gray-50 p-12 rounded-[56px] border border-gray-100 space-y-6">
                            <h3 className="text-2xl font-black text-gray-900 border-l-4 border-green-700 pl-4 uppercase">Specifications</h3>
                            <div className="space-y-4">
                                <p className="flex justify-between font-bold text-lg"><span className="text-gray-400">Surface</span> <span className="text-gray-900 uppercase">Grass</span></p>
                                <p className="flex justify-between font-bold text-lg"><span className="text-gray-400">Capacity</span> <span className="text-gray-900 uppercase">5,000 Seats</span></p>
                                <p className="flex justify-between font-bold text-lg"><span className="text-gray-400">Parking</span> <span className="text-gray-900 uppercase">Available</span></p>
                            </div>
                        </div>

                        <div className="lg:col-span-2 bg-gray-900 p-12 rounded-[56px] text-white flex flex-col justify-center items-center text-center space-y-8 relative overflow-hidden group">
                            <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-green-700/30 rounded-full group-hover:scale-150 transition-transform duration-700" />
                            <h3 className="text-4xl font-black relative z-10">Professional Standards. <br />Community Spirit.</h3>
                            <p className="text-white/60 text-lg font-bold italic max-w-xl relative z-10 uppercase tracking-widest">Experience the best venues Nigeria has to offer, maintained to international sports federation standards.</p>
                        </div>
                    </div>
                </div>
            </div>
            <Banner />
            <Footer />
        </>
    );
};

export default DetailedPitch;
